import type { FlatSurface, ShapePoint, ShapeVertex } from '@repo/api-contracts';
import { type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  clientToPhotoPoint,
  deleteVertexAt,
  insertVertexAt,
  moveHandleTo,
  moveVertexTo,
  nearestPointOnRing,
  type PhotoDimensions,
  surfacePhotoToMm,
  toggleVertexSmooth,
  type VertexHandle,
} from '@/entities/edit-area';

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  /** 점 편집 중인 영역의 평면 표면. 점 편집이 꺼져 있으면 undefined. */
  surface: FlatSurface | undefined;
  /** 점 편집 중인 영역의 바깥 고리 점(표면 mm). 점 편집이 꺼져 있으면 null. */
  points: readonly ShapeVertex[] | null;
  /** 고친 점열(표면 mm)을 반영한다. 반영하면 null, 거부되면 사유(그때는 원래 모양 그대로다). */
  onCommit: (points: ShapeVertex[]) => string | null;
  /**
   * 점 편집 대상(바깥 고리·구멍 순번 등)을 가리키는 값. 바뀌면 끌기·고른 점·거부 사유를 거둔다 — 같은 영역에서 바깥 고리와
   * 구멍 사이를 오갈 때 지난 대상의 점 순번이 남지 않게 한다.
   */
  targetKey?: string;
  /** 점이 최소 개수라 지울 수 없을 때의 사유. */
  minPointsMessage?: string;
}

type DragTarget =
  | { readonly kind: 'vertex'; readonly index: number }
  | { readonly kind: 'handle'; readonly index: number; readonly which: VertexHandle };

interface Drag {
  readonly pointerId: number;
  readonly target: DragTarget;
  readonly client: { readonly x: number; readonly y: number };
  /** 누른 자리의 표면 mm — 끄는 동안 이 점에서 옮긴 만큼 점·손잡이를 옮긴다. */
  readonly origin: ShapePoint;
  readonly base: readonly ShapeVertex[];
  readonly last: ShapeVertex[] | null;
  /** 흔들림을 넘게 움직여 포인터를 잡았는가. 누르기만 하면 잡지 않아 click·dblclick 이 누른 요소에 그대로 간다. */
  readonly captured: boolean;
}

/** 옮기지 않고 누른 점 — 같은 점을 곧바로 다시 누르면 더블클릭(부드러운 점 ↔ 뾰족한 점)으로 본다. */
interface Press {
  readonly index: number;
  readonly at: number;
  /** 누를 때의 점열(직렬화) — 그 사이 점열이 바뀌었으면(지우기·되돌리기 등) 더블클릭으로 보지 않는다. */
  readonly signature: string;
}

/** 고른 점과 고를 때의 점 개수 — 바깥에서 점 개수가 바뀌면(되돌리기 등) 고른 점을 버린다. */
interface Selection {
  readonly index: number;
  readonly length: number;
}

/** 누르기만 한 것으로 보는 포인터 흔들림(화면 px). */
const CLICK_SLOP_PX = 3;
/** 같은 점을 이 시간(ms) 안에 다시 누르면 더블클릭으로 본다. */
const DOUBLE_PRESS_MS = 400;
/** 구간 끝에 이만큼(t) 가까우면 점을 더하지 않는다 — 기존 점을 누른 것과 같다. */
const END_T_MARGIN = 1e-3;
const DEFAULT_MIN_POINTS_MESSAGE = '인쇄 영역 점은 최소 3개가 있어야 합니다.';

/**
 * 점 편집 — 점·손잡이 끌기(표면 mm 로 미리 보이고 놓을 때 한 번 반영), 점 더블클릭으로 부드러운 점 ↔ 뾰족한 점,
 * 선 위 누르기로 점 더하기, 고른 점 지우기. 반영이 거부되면 미리보기를 버리고 사유를 보인다(다음 조작·끝낼 때 지운다).
 *
 * 포인터는 흔들림(3px)을 넘게 움직일 때에야 svg 로 잡는다. 더블클릭은 DOM dblclick 이 아니라 이 훅이 판단한다 —
 * 옮기지 않고 같은 점을 400ms 안에 두 번 누르면 부드러운 점 ↔ 뾰족한 점을 바꾼다(브라우저마다 dblclick 이 어느 요소로
 * 가는지에 기대지 않는다).
 */
export const usePointEditPointer = ({
  svgRef,
  photoSize,
  surface,
  points,
  onCommit,
  targetKey = '',
  minPointsMessage = DEFAULT_MIN_POINTS_MESSAGE,
}: Options) => {
  const drag = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<ShapeVertex[] | null>(null);
  const lastPress = useRef<Press | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = surface !== undefined && points !== null;
  // 고른 뒤 바깥에서 점 개수가 바뀌었거나 범위를 벗어나면 고른 점이 없는 것으로 본다(엉뚱한 점을 지우지 않게).
  const selectedIndex =
    selection !== null &&
    points !== null &&
    selection.length === points.length &&
    selection.index < points.length
      ? selection.index
      : null;
  const select = (index: number, length: number): void => setSelection({ index, length });

  const cancel = useCallback((): void => {
    const pointerId = drag.current?.pointerId;
    drag.current = null;
    setPreview(null);
    if (pointerId !== undefined && svgRef.current?.hasPointerCapture(pointerId))
      svgRef.current.releasePointerCapture(pointerId);
  }, [svgRef]);
  // 점 편집을 끝내면 끌기·고른 점·거부 사유를 모두 거둔다.
  useEffect(() => {
    if (active) return;
    cancel();
    setSelection(null);
    setError(null);
    lastPress.current = null;
  }, [active, cancel]);
  // 대상이 바뀌면(바깥 고리 ↔ 구멍) 지난 대상의 끌기·고른 점·사유를 거둔다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 대상이 바뀔 때마다 거둔다.
  useEffect(() => {
    cancel();
    setSelection(null);
    setError(null);
    lastPress.current = null;
  }, [targetKey, cancel]);

  const mmAt = (event: PointerEvent<SVGElement> | { clientX: number; clientY: number }) => {
    if (surface === undefined || svgRef.current === null) return null;
    const point = clientToPhotoPoint(svgRef.current, { x: event.clientX, y: event.clientY });
    return point === null ? null : surfacePhotoToMm(surface, photoSize, point);
  };
  const commit = (next: ShapeVertex[]): boolean => {
    const rejection = onCommit(next);
    setError(rejection);
    return rejection === null;
  };

  const begin = (event: PointerEvent<SVGElement>, target: DragTarget): void => {
    if (!active || points === null || event.button !== 0 || svgRef.current === null) return;
    event.preventDefault();
    event.stopPropagation();
    setError(null);
    const origin = mmAt(event);
    if (origin === null) return;
    drag.current = {
      pointerId: event.pointerId,
      target,
      client: { x: event.clientX, y: event.clientY },
      origin,
      base: points,
      last: null,
      captured: false,
    };
  };
  const startVertex = (event: PointerEvent<SVGElement>, index: number): void => {
    if (!active || points === null || event.button !== 0) return;
    select(index, points.length);
    begin(event, { kind: 'vertex', index });
  };
  const startHandle = (event: PointerEvent<SVGElement>, index: number, which: VertexHandle) =>
    begin(event, { kind: 'handle', index, which });

  /** 끄는 포인터 이벤트로 바꾼 점열. 다른 포인터면 null, 표면 mm 로 옮길 수 없으면 마지막 점열. */
  const pointsAt = (event: PointerEvent<SVGElement>): ShapeVertex[] | null => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return null;
    const to = mmAt(event);
    if (to === null) return current.last;
    const dx = to.xMm - current.origin.xMm;
    const dy = to.yMm - current.origin.yMm;
    const { target, base } = current;
    const vertex = base[target.index];
    if (vertex === undefined) return current.last;
    if (target.kind === 'vertex')
      return moveVertexTo(base, target.index, { xMm: vertex.xMm + dx, yMm: vertex.yMm + dy });
    const handle = vertex[target.which] ?? vertex;
    return moveHandleTo(
      base,
      target.index,
      target.which,
      { xMm: handle.xMm + dx, yMm: handle.yMm + dy },
      { independent: event.altKey },
    );
  };
  const movedFar = (current: Drag, event: { clientX: number; clientY: number }): boolean =>
    Math.hypot(event.clientX - current.client.x, event.clientY - current.client.y) > CLICK_SLOP_PX;
  const move = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return;
    // 흔들림 안에서는 미리 보지도, 포인터를 잡지도 않는다 — 누르기만 한 것이다.
    if (!current.captured && !movedFar(current, event)) return;
    const next = pointsAt(event);
    if (next === null) return;
    if (!current.captured) svgRef.current?.setPointerCapture(current.pointerId);
    drag.current = { ...current, last: next, captured: true };
    setPreview(next);
  };
  const finish = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return;
    const moved = current.captured || movedFar(current, event);
    const next = moved ? (pointsAt(event) ?? current.last) : null;
    cancel();
    if (moved) {
      lastPress.current = null;
      if (next !== null) commit(next);
      return;
    }
    if (current.target.kind !== 'vertex') {
      lastPress.current = null;
      return;
    }
    const now = performance.now();
    const previous = lastPress.current;
    if (
      previous !== null &&
      previous.index === current.target.index &&
      previous.signature === JSON.stringify(current.base) &&
      now - previous.at <= DOUBLE_PRESS_MS
    ) {
      lastPress.current = null;
      toggleSmooth(current.target.index);
      return;
    }
    lastPress.current = {
      index: current.target.index,
      at: now,
      signature: JSON.stringify(current.base),
    };
  };

  /** 점 더블클릭(또는 곡선/직선 단추) — 부드러운 점 ↔ 뾰족한 점. */
  const toggleSmooth = (index: number): void => {
    if (!active || points === null) return;
    cancel();
    lastPress.current = null;
    select(index, points.length);
    const next = toggleVertexSmooth(points, index);
    if (next === null) {
      setError('앞뒤 점이 같은 자리라 곡선으로 바꿀 수 없습니다.');
      return;
    }
    commit(next);
  };
  /** 선 위 누르기 — 가장 가까운 구간의 그 자리에 점을 더하고 고른다. */
  const insertAt = (event: PointerEvent<SVGElement>): void => {
    if (!active || points === null || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    setError(null);
    const at = mmAt(event);
    const hit = at === null ? null : nearestPointOnRing(points, at);
    if (hit === null || hit.t <= END_T_MARGIN || hit.t >= 1 - END_T_MARGIN) return;
    const next = insertVertexAt(points, hit.segmentIndex, hit.t);
    if (next === null) return;
    lastPress.current = null;
    if (commit(next)) select(hit.segmentIndex + 1, next.length);
  };
  /** 고른 점을 지운다. 고른 점이 있어 키를 처리했으면 true(최소 개수라 지우지 못해도 true — 사유를 보인다). */
  const deleteSelected = (): boolean => {
    if (!active || points === null || selectedIndex === null) return false;
    cancel();
    lastPress.current = null;
    const next = deleteVertexAt(points, selectedIndex);
    if (next === null) {
      setError(minPointsMessage);
      return true;
    }
    if (commit(next)) setSelection(null);
    return true;
  };

  return {
    /** 끄는 중이면 미리보기, 아니면 들어온 점열. */
    points: preview ?? points,
    dragging: preview !== null,
    selectedIndex,
    error,
    startVertex,
    startHandle,
    move,
    finish,
    cancel,
    toggleSmooth,
    insertAt,
    deleteSelected,
  };
};
